import React from "react";
import { Models } from "@hex-insights/app-modules";
import { Button, Column, doNothing, Grid, Row, Rule } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanField,
	DateTimeField,
	FileUploadField,
	Label,
	SubFormField,
	SubFormFieldContentProps,
	SubFormFieldItemsProps,
	SubFormRenderProps,
	TextField,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { ContractFormValues, ContractInstallmentFormValues, ContractLineItemFormValues } from "../../FormUtilities";
import { AcademicTermSelectField } from "../AcademicTerm";
import { ContractInstallmentForm } from "../ContractInstallment";
import { ContractLineItemForm } from "../ContractLineItem";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { UserSelectField } from "../User";
import styles from "./field.module.css";

/**
 * Generic props for fields of the Contract model.
 */
type FieldProps<K extends keyof ContractFormValues.Base = keyof ContractFormValues.Base> = BaseFieldProps<
	Pick<ContractFormValues.Base, K>
>;

/**
 * Generic props for fields of the Contract model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof ContractFormValues.Create = keyof ContractFormValues.Create> = BaseFieldProps<
	Pick<ContractFormValues.Create, K>
>;

/**
 * Generic props for fields of the Contract model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof ContractFormValues.Detail = keyof ContractFormValues.Detail> = BaseFieldProps<
	Pick<ContractFormValues.Detail, K>
>;

/**
 * Field component for the `name` field of the Contract model.
 */
export function Name({ formState, label = "Contract ID", ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" label={label} {...props} />;
}

/**
 * Field component for the `status` field of the Contract model.
 */
export function Status({ formState, ...props }: DetailFieldProps<"status">) {
	return <TextField formState={formState} name="status" {...props} />;
}

/**
 * Field component for the `isFinalized` field of the Contract model.
 */
export function IsFinalized({ formState, ...props }: FieldProps<"isFinalized">) {
	return <BooleanField formState={formState} name="isFinalized" {...props} />;
}

/**
 * Field component for the `effectiveDate` field of the Contract model.
 */
export function EffectiveDate({ formState, ...props }: FieldProps<"effectiveDate">) {
	return <DateTimeField formState={formState} name="effectiveDate" optional precision="day" {...props} />;
}

/**
 * Field component for the `dateVoided` field of the Contract model.
 */
export function DateVoided({ formState, ...props }: FieldProps<"dateVoided">) {
	return <DateTimeField formState={formState} name="dateVoided" optional precision="day" {...props} />;
}

/**
 * Field component for the `isApproved` field of the Contract model.
 */
export function IsApproved({ formState, ...props }: FieldProps<"isApproved">) {
	return <BooleanField formState={formState} name="isApproved" {...props} />;
}

const fileTypes = ["application/pdf"];

/**
 * Field component for the `file` field of the Contract model.
 */
export function File({ formState, url, ...props }: FieldProps<"fileKey" | "fileContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "File");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="fileKey"
			contentTypeFieldName="fileContentType"
			url={url}
			optional
			accept={fileTypes}
			{...props}
		/>
	);
}

/**
 * Field component for the `signedFile` field of the Contract model.
 */
export function SignedFile({
	formState,
	url,
	...props
}: FieldProps<"signedFileKey" | "signedFileContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "SignedFile");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="signedFileKey"
			contentTypeFieldName="signedFileContentType"
			url={url}
			optional
			accept={fileTypes}
			{...props}
		/>
	);
}

export namespace AcademicTerms {
	export type SelectProps = FieldProps<"academicTermIDs"> &
		Omit<AcademicTermSelectField.MultiSelectProps<ContractFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `academicTerms` edge of the Contract model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AcademicTermSelectField.MultiSelect formState={formState} name="academicTermIDs" {...props} />;
	}
}

export namespace ApprovingUser {
	export type SelectProps = DetailFieldProps<"approvingUserID"> &
		Omit<UserSelectField.SelectProps<ContractFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `approvingUser` edge of the Contract model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="approvingUserID" optional {...props} />;
	}
}

export namespace ContractInstallments {
	/**
	 * SubForm field component for the `contractInstallments` edge of the Contract model.
	 */
	export function SubForm({ formState, className, style, ...props }: CreateFieldProps<"contractInstallments">) {
		const totalPortion = formState.formValues.contractInstallments.reduce((a, e) => a + (e.portion ?? 0), 0);
		const WrappedSubForm = React.useCallback(
			(props: SubFormRenderProps<ContractInstallmentFormValues.CreateInContract>) => {
				return <SubForm.SubForm {...props} totalPortion={totalPortion} />;
			},
			[totalPortion],
		);

		const { contractInstallments: setContractInstallments } = formState.formSetFunctions;
		const setPredefinedContractInstallments = React.useCallback(
			(numInstallments: number) => {
				// TODO generalize
				switch (numInstallments) {
					case 1:
						setContractInstallments([
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-08-01T00:00:00Z" },
						]);
						break;
					case 2:
						setContractInstallments([
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-08-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-02-01T00:00:00Z" },
						]);
						break;
					case 4:
						setContractInstallments([
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-08-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-11-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-02-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-04-01T00:00:00Z" },
						]);
						break;
					case 9:
						setContractInstallments([
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-08-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-09-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-10-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-11-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2023-12-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-01-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-02-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-03-01T00:00:00Z" },
							{ ...ContractInstallmentFormValues.blankItemInContract, dueDate: "2024-04-01T00:00:00Z" },
						]);
						break;
				}
			},
			[setContractInstallments],
		);

		const numInstallments = formState.formValues.contractInstallments.length;
		const isCustom = numInstallments !== 1 && numInstallments !== 2 && numInstallments !== 4 && numInstallments !== 9;

		return (
			<Column justify="spaced-start" className={className} style={{ width: "100%", ...style }}>
				<Row justify="spaced-start" horizontalSpacing="0.5rem" overflow="wrap" withVerticalSpacing>
					<Button
						variant={numInstallments === 1 ? "primary" : "tertiary"}
						onClick={() => setPredefinedContractInstallments(1)}
					>
						One Payment
					</Button>
					<Button
						variant={numInstallments === 2 ? "primary" : "tertiary"}
						onClick={() => setPredefinedContractInstallments(2)}
					>
						Two Payments
					</Button>
					<Button
						variant={numInstallments === 4 ? "primary" : "tertiary"}
						onClick={() => setPredefinedContractInstallments(4)}
					>
						Four Payments
					</Button>
					<Button
						variant={numInstallments === 9 ? "primary" : "tertiary"}
						onClick={() => setPredefinedContractInstallments(9)}
					>
						Nine Payments
					</Button>
					<Button variant={isCustom ? "primary" : "tertiary"} onClick={doNothing}>
						Custom
					</Button>
				</Row>

				<SubFormField
					formState={formState}
					name="contractInstallments"
					blankItem={ContractInstallmentFormValues.blankItemInContract}
					Content={SubForm.Content}
					Items={SubForm.Items}
					dividerElement={<Rule />}
					minItems={1}
					style={{ width: "100%", "--sub-form-field__value---padding": 0 } as React.CSSProperties}
					{...props}
				>
					{WrappedSubForm}
					{SubForm.AddButton}
				</SubFormField>
			</Column>
		);
	}

	const gridTemplateTracks = "var(--general__field---width) 10rem 1fr";
	const gridGap = "1rem";

	SubForm.Content = function ({
		children,
	}: SubFormFieldContentProps<
		Pick<ContractFormValues.Create, "contractInstallments">,
		ContractInstallmentFormValues.CreateInContract
	>) {
		return (
			<Column justify="spaced-start" className={styles["contract-line-items__content"]}>
				<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
					<Label>Due Date</Label>
					<Label>Portion</Label>
				</Grid>

				<Rule />

				{children}
			</Column>
		);
	};

	SubForm.Items = function ({
		children,
	}: SubFormFieldItemsProps<
		Pick<ContractFormValues.Create, "contractInstallments">,
		ContractInstallmentFormValues.CreateInContract
	>) {
		return <React.Fragment>{children}</React.Fragment>;
	};

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		totalPortion,
	}: SubFormRenderProps<ContractInstallmentFormValues.CreateInContract> & { totalPortion: number }) {
		return (
			<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
				<ContractInstallmentForm.CreateFieldsInContract formState={formState} totalPortion={totalPortion} />
				<Row justify="flex-end">
					<Button
						variant="tertiary"
						size="small"
						onClick={onRemoveClick}
						disabled={formState.disabled}
						className="button--icon"
					>
						<Icon.TrashSimple size={16} className="block" />
					</Button>
				</Row>
			</Grid>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<ContractFormValues.Create, "contractInstallments">,
		ContractInstallmentFormValues.CreateInContract
	>) {
		return (
			<React.Fragment>
				<Rule />

				<Button variant="link" size="small" onClick={onClick} disabled={disabled}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.Plus size={16} /> Add Installment
					</Row>
				</Button>
			</React.Fragment>
		);
	};
}

export namespace ContractLineItems {
	/**
	 * SubForm field component for the `contractLineItems` edge of the Contract model.
	 */
	export function SubForm({ formState, label = "Services", style, ...props }: CreateFieldProps<"contractLineItems">) {
		return (
			<SubFormField
				formState={formState}
				name="contractLineItems"
				label={label}
				blankItem={ContractLineItemFormValues.blankItemInContract}
				Content={SubForm.Content}
				Items={SubForm.Items}
				dividerElement={<Rule />}
				minItems={1}
				style={{ width: "100%", "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	const gridTemplateTracks = "var(--general__field---width) 10rem 1fr 2rem";
	const gridGap = "1rem";

	SubForm.Content = function ({
		children,
	}: SubFormFieldContentProps<
		Pick<ContractFormValues.Create, "contractLineItems">,
		ContractLineItemFormValues.CreateInContract
	>) {
		return (
			<Column justify="spaced-start" className={styles["contract-line-items__content"]}>
				<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
					<Label>Service</Label>
					<Label>Price</Label>
					<Label>Discount</Label>
				</Grid>

				<Rule />

				{children}
			</Column>
		);
	};

	SubForm.Items = function ({
		children,
	}: SubFormFieldItemsProps<
		Pick<ContractFormValues.Create, "contractLineItems">,
		ContractLineItemFormValues.CreateInContract
	>) {
		return <React.Fragment>{children}</React.Fragment>;
	};

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<ContractLineItemFormValues.CreateInContract>) {
		return (
			<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
				<ContractLineItemForm.CreateFieldsInContract formState={formState} />
				<Row justify="flex-end">
					<Button
						variant="tertiary"
						size="small"
						onClick={onRemoveClick}
						disabled={formState.disabled}
						className="button--icon"
					>
						<Icon.TrashSimple size={16} className="block" />
					</Button>
				</Row>
			</Grid>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<ContractFormValues.Create, "contractLineItems">,
		ContractLineItemFormValues.CreateInContract
	>) {
		return (
			<React.Fragment>
				<Rule />

				<Button variant="link" size="small" onClick={onClick} disabled={disabled}>
					<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
						<Icon.Plus size={16} /> Add Service
					</Row>
				</Button>
			</React.Fragment>
		);
	};
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<ContractFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the Contract model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
